import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PROVIDERS as PROVIDER_REGISTRY } from '@sequesto-os/shared';
import { useIntegrations } from '../hooks/useIntegrations';
import type { Integration } from '../api/integrations';
import { EmptyState, ErrorState, LoadingState } from './ui/States';
import { formatDayMonthYearTime } from '../utils/dateFormat';
import { failureCause } from '../pages/Integrations/syncError';

/**
 * One displayed status per integration. The API derives it (GET /integrations
 * → `health`) from three signals: is it configured, the latest sync run, and
 * the latest run of each automation that uses it. Integrations, the Home
 * exception line and the sidebar footer all read it from here.
 */
export type IntegrationHealth =
  | 'failing'
  | 'stuck'
  | 'syncing'
  | 'ok'
  | 'key_set'
  | 'pending'
  | 'disabled'
  | 'not_connected';

export interface DerivedHealth {
  state: IntegrationHealth;
  reason: string | null;
  since: string | null;
  source: string | null;
  /** `by` names what used it ("Sales research", an automation). */
  lastActivity: { at: string; kind: 'sync' | 'automation' | 'usage'; by?: string | null } | null;
  configuredVia: 'row' | 'secret' | 'env' | null;
  hasSync: boolean;
  /** The OS records this tool's use (a sync or a mapped automation); without it, no last use is not "unused". */
  usageTracked?: boolean;
  /** No OS code calls this tool: its key is there for people or an MCP (Clay, DeepL). */
  notCalledByOs?: boolean;
}

/**
 * A key-only tool the OS has seen used is Connected, like a synced one; "Key
 * set" is kept for keys with no recorded use. The API applies the same rule
 * (status.ts); this covers an API from before it did.
 */
export function normalizeHealth(h: DerivedHealth): DerivedHealth {
  return h.state === 'key_set' && h.lastActivity ? { ...h, state: 'ok', reason: null, since: null, source: null } : h;
}

/** GET /integrations, with the status fields the shared Integration type does not declare. */
export interface IntegrationsPayload {
  integrations: (Integration & { health?: DerivedHealth })[];
  unconnected?: { provider: string; health: DerivedHealth }[];
  statusWarnings?: string[];
}

const STUCK_AFTER_MS = 60 * 60 * 1000;

/**
 * The integration's status. Uses the API's derived `health`; an older API
 * without it falls back to the row's own fields under the same rules.
 */
export function integrationStatus(i: Integration): DerivedHealth {
  const derived = (i as Integration & { health?: DerivedHealth }).health;
  if (derived) return normalizeHealth(derived);
  const base = {
    reason: null,
    since: null,
    source: null,
    lastActivity: i.lastSyncAt ? { at: i.lastSyncAt, kind: 'sync' as const } : null,
    configuredVia: 'row' as const,
    hasSync: true,
    usageTracked: true,
  };
  if (!i.isEnabled) return { ...base, state: 'disabled' };
  if (i.status === 'error') return { ...base, state: 'failing', reason: i.lastSyncError };
  if (i.lastSyncStatus === 'running') {
    const stuck = !!i.lastSyncAt && Date.now() - new Date(i.lastSyncAt).getTime() > STUCK_AFTER_MS;
    return { ...base, state: stuck ? 'stuck' : 'syncing', since: i.lastSyncAt };
  }
  if (i.lastSyncStatus === 'failed') return { ...base, state: 'failing', reason: i.lastSyncError };
  if (i.status === 'pending') return { ...base, state: 'pending' };
  return { ...base, state: 'ok' };
}

export function integrationHealth(i: Integration): IntegrationHealth {
  return integrationStatus(i).state;
}

export const isExceptionHealth = (h: IntegrationHealth) => h === 'failing' || h === 'stuck';

export interface IntegrationException {
  key: string;
  /** The row to open, when the integration has one. */
  integration: Integration | null;
  name: string;
  health: DerivedHealth;
}

/**
 * Everything that needs a look, from the one Integrations query: failing and
 * stuck rows first, then providers with no connection that an automation still
 * runs for ("UptimeRobot poll runs but skips"). Shared by the Integrations
 * strip, Home and the sidebar so the three never disagree.
 */
export function useIntegrationExceptions(enabled = true) {
  const query = useIntegrations(enabled);
  const payload = query.data as IntegrationsPayload | undefined;
  const result = useMemo(() => {
    const rows = payload?.integrations ?? [];
    const exceptions: IntegrationException[] = rows
      .map((i) => ({ key: i.id, integration: i as Integration, name: integrationDisplayName(i), health: integrationStatus(i) }))
      .filter((e) => isExceptionHealth(e.health.state))
      .sort((a, b) => Number(b.health.state === 'failing') - Number(a.health.state === 'failing') || a.name.localeCompare(b.name));
    const notices: IntegrationException[] = (payload?.unconnected ?? [])
      .filter((u) => u.health.state === 'not_connected' && u.health.reason)
      .map((u) => ({
        key: u.provider,
        integration: null,
        name: PROVIDER_REGISTRY[u.provider as keyof typeof PROVIDER_REGISTRY]?.name ?? u.provider,
        health: u.health,
      }));
    return { exceptions, notices, warnings: payload?.statusWarnings ?? [] };
  }, [payload]);
  return { ...result, isLoading: query.isLoading, error: query.error, data: payload, refetch: query.refetch };
}

export const EMPTY_COUNTS: Record<IntegrationHealth, number> = {
  failing: 0, stuck: 0, syncing: 0, ok: 0, key_set: 0, pending: 0, disabled: 0, not_connected: 0,
};

/** Order the header and legend list states in: problems first, then healthy. */
export const COUNT_ORDER: IntegrationHealth[] = ['failing', 'stuck', 'syncing', 'pending', 'ok', 'key_set', 'disabled'];

/**
 * One status per configured integration: every connection row plus every key
 * with no row (DeepL). The Integrations header and the diagram legend both
 * count from this, so their categories are the same and add up to `total`.
 */
export function healthCounts(payload: IntegrationsPayload | undefined): { total: number; counts: Record<IntegrationHealth, number> } {
  const states = [
    ...(payload?.integrations ?? []).map((i) => integrationStatus(i).state),
    ...(payload?.unconnected ?? []).filter((u) => u.health.configuredVia).map((u) => normalizeHealth(u.health).state),
  ];
  const counts = { ...EMPTY_COUNTS };
  for (const st of states) counts[st] += 1;
  return { total: states.length, counts };
}

const squash = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * The name to show for an integration. Auto-named rows ("Openai", "Google
 * search_console") take the provider's proper name; names someone chose
 * ("Calendly Andrew") are kept.
 */
export function integrationDisplayName(i: Pick<Integration, 'name' | 'provider'>): string {
  const def = PROVIDER_REGISTRY[i.provider as keyof typeof PROVIDER_REGISTRY];
  if (!def) return i.name;
  const n = squash(i.name);
  return n === squash(i.provider) || n === squash(def.name) ? def.name : i.name;
}

export const HEALTH_LABEL: Record<IntegrationHealth, string> = {
  failing: 'Failing',
  stuck: 'Stuck',
  syncing: 'Syncing',
  // The same words the Integrations header and status column use.
  ok: 'Connected',
  key_set: 'Key set, no use seen',
  pending: 'Pending',
  disabled: 'Disabled',
  not_connected: 'Not connected',
};

export const HEALTH_DOT: Record<IntegrationHealth, string> = {
  failing: 'bg-error',
  stuck: 'bg-warning',
  syncing: 'bg-text-tertiary',
  ok: 'bg-success',
  // Not proven working, not broken: neutral.
  key_set: 'bg-text-muted',
  pending: 'bg-warning',
  disabled: 'bg-text-muted',
  not_connected: 'bg-text-muted',
};

// SVG colours follow the tailwind tokens (error / success / warning / stone).
// Syncing is a neutral stone pulse: indigo is reserved for links and focus.
const NODE_FILL: Record<IntegrationHealth, string> = {
  failing: '#C4574A',
  stuck: '#D99A1C',
  syncing: '#6D6963',
  ok: '#2E9E62',
  key_set: '#A8A29A',
  pending: '#D99A1C',
  disabled: '#A8A29A',
  not_connected: '#A8A29A',
};

/**
 * The one category set for the diagram and the table filters: the team a
 * provider serves. A provider tagged for several teams sits in the diagram
 * under its first tag and matches every one of its tags in the filter.
 * Untagged tools (AI keys, infrastructure, Slack) are "Platform".
 */
export const TEAM_CATEGORIES = [
  { id: 'sales', label: 'Sales' },
  { id: 'marketing', label: 'Marketing' },
  { id: 'content', label: 'Content' },
  { id: 'finance', label: 'Finance' },
  { id: 'cs', label: 'CS' },
  { id: 'platform', label: 'Platform' },
] as const;
export type TeamCategory = (typeof TEAM_CATEGORIES)[number]['id'];

export function providerTeams(provider: string): TeamCategory[] {
  const tags = PROVIDER_REGISTRY[provider as keyof typeof PROVIDER_REGISTRY]?.tags ?? [];
  return tags.length > 0 ? [...tags] : ['platform'];
}

const TEAM_LABEL: Record<TeamCategory, string> = Object.fromEntries(
  TEAM_CATEGORIES.map((c) => [c.id, c.label]),
) as Record<TeamCategory, string>;


const W = 880; // wide enough that full names fit beside the ring
const C = 340; // ring centre y (x centre is W / 2)
const CX = W / 2;
const R = 180; // node ring
const ARC_R = 162; // category arcs, just inside the ring
const MARK_R = ARC_R - 14; // category number markers, inside the arcs
const LABEL_R = R + 16; // where outer labels hug the ring
const LABEL_GAP_Y = 15; // min vertical distance between two outer labels
const MAX_LABEL = 30;
const GROUP_GAP = 0.6; // extra slot of space between category groups
const MARK_CLEAR = 0.06; // radians a marker keeps from a red spoke (~9px at MARK_R)
const MARK_SPACING = 0.1; // radians between two markers (~15px at MARK_R)

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

function polar(r: number, angle: number) {
  return { x: CX + r * Math.cos(angle), y: C + r * Math.sin(angle) };
}

function arcPath(r: number, a0: number, a1: number): string {
  const p0 = polar(r, a0);
  const p1 = polar(r, a1);
  const large = a1 - a0 > Math.PI ? 1 : 0;
  return `M${p0.x},${p0.y} A${r},${r} 0 ${large} 1 ${p1.x},${p1.y}`;
}

/**
 * Horizontal labels in two columns (left and right of the ring). Each column is
 * spread vertically so no two labels overlap, then each label's x follows the
 * circle of radius `r` at its new y, so the labels still hug the ring.
 */
function layoutLabels<T extends { angle: number }>(items: T[], r: number, gap: number) {
  const out = new Map<T, { x: number; y: number; side: 1 | -1 }>();
  for (const side of [1, -1] as const) {
    const column = items
      .filter((i) => (Math.cos(i.angle) >= 0 ? 1 : -1) === side)
      .map((i) => ({ item: i, y: C + r * Math.sin(i.angle) }))
      .sort((a, b) => a.y - b.y);
    if (column.length === 0) continue;
    const target = column.map((c) => c.y);
    // Push down to clear overlaps, then pull back up from below, so the column
    // stays centred on where its nodes actually are.
    for (let i = 1; i < column.length; i++) column[i].y = Math.max(column[i].y, column[i - 1].y + gap);
    const overflow = column[column.length - 1].y - Math.max(target[target.length - 1], C + r);
    if (overflow > 0) for (const c of column) c.y -= overflow / 2;
    for (let i = column.length - 2; i >= 0; i--) column[i].y = Math.min(column[i].y, column[i + 1].y - gap);
    for (const c of column) {
      const dy = c.y - C;
      const dx = Math.sqrt(Math.max(0, r * r - dy * dy));
      out.set(c.item, { x: CX + side * Math.max(dx, 12), y: c.y, side });
    }
  }
  return out;
}

interface Node {
  id: string;
  name: string;
  providerName: string;
  category: string;
  team: string;
  health: IntegrationHealth;
  reason: string | null;
  lastActivityAt: string | null;
  angle: number;
}

/**
 * Radial map of every connected integration around the OS hub. Nodes are
 * grouped by team, the same set the table filters use (a gap and a numbered arc mark each group; the
 * names and counts sit in a legend beside the ring, so no category text ever
 * competes with node labels or spokes) and labelled horizontally in two spread
 * columns so 40+ names never overlap. Only failing integrations get a spoke to
 * the hub, drawn red. Hovering a category (arc, node or legend row) highlights it.
 *
 * Provider defs carry no logo or domain, so nodes are status dots.
 */
export function SystemDiagram({ onSelect }: { onSelect?: (integration: Integration) => void }) {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useIntegrations();
  const integrations = useMemo(() => data?.integrations ?? [], [data]);
  // Keys in the secret store with no connection row (DeepL) count as connected
  // on the page header, so the ring, legend and status counts include them too.
  const keyOnly = useMemo(
    () =>
      ((data as IntegrationsPayload | undefined)?.unconnected ?? [])
        .filter((u) => u.health.configuredVia)
        .map((u) => ({ ...u, health: normalizeHealth(u.health) })),
    [data],
  );
  const [hovered, setHovered] = useState<string | null>(null);

  const { nodes, groups } = useMemo(() => {
    const order = TEAM_CATEGORIES.map((c) => c.id as string);
    const keyOnlyItems = keyOnly.map((u) => {
      const def = PROVIDER_REGISTRY[u.provider as keyof typeof PROVIDER_REGISTRY];
      const team = providerTeams(u.provider)[0];
      return {
        id: `key:${u.provider}`,
        name: def?.name ?? u.provider,
        providerName: def?.name ?? u.provider,
        category: TEAM_LABEL[team],
        team: team as string,
        health: u.health.state,
        reason: u.health.reason,
        lastActivityAt: u.health.lastActivity?.at ?? null,
      };
    });
    const items = integrations
      .map((i) => {
        const def = PROVIDER_REGISTRY[i.provider as keyof typeof PROVIDER_REGISTRY];
        const status = integrationStatus(i);
        return {
          id: i.id,
          name: integrationDisplayName(i),
          providerName: def?.name ?? i.provider,
          category: TEAM_LABEL[providerTeams(i.provider)[0]],
          team: providerTeams(i.provider)[0] as string,
          health: status.state,
          reason: status.reason,
          lastActivityAt: status.lastActivity?.at ?? null,
        };
      })
      .concat(keyOnlyItems)
      .sort((a, b) => order.indexOf(a.team) - order.indexOf(b.team) || a.name.localeCompare(b.name));

    const categories = [...new Set(items.map((i) => i.category))];
    const slots = items.length + categories.length * GROUP_GAP;
    const step = (2 * Math.PI) / Math.max(slots, 1);

    let cursor = -Math.PI / 2 + (step * GROUP_GAP) / 2;
    const nodes: Node[] = [];
    const groups: { category: string; start: number; end: number; count: number }[] = [];
    for (const category of categories) {
      const members = items.filter((i) => i.category === category);
      const start = cursor;
      for (const m of members) {
        nodes.push({ ...m, angle: cursor + step / 2 });
        cursor += step;
      }
      groups.push({ category, start, end: cursor, count: members.length });
      cursor += step * GROUP_GAP;
    }
    return { nodes, groups };
  }, [integrations, keyOnly]);

  if (isLoading) return <LoadingState rows={4} label="Loading systems…" />;
  if (error) return <ErrorState title="Couldn’t load integrations" error={error} onRetry={() => refetch()} />;
  if (nodes.length === 0) {
    return <EmptyState title="No integrations connected yet" description="Add one below to see it here." />;
  }

  const counts = nodes.reduce<Record<IntegrationHealth, number>>(
    (acc, n) => ({ ...acc, [n.health]: acc[n.health] + 1 }),
    { ...EMPTY_COUNTS }
  );
  const legend = COUNT_ORDER.filter((h) => counts[h] > 0);

  const select = (id: string) => {
    const integration = integrations.find((i) => i.id === id);
    if (integration && onSelect) onSelect(integration);
    else navigate('/integrations');
  };

  const nodeLabels = layoutLabels(nodes, LABEL_R, LABEL_GAP_Y);
  const spokeAngles = nodes.filter((n) => n.health === 'failing').map((n) => n.angle);
  const angleDist = (a: number, b: number) => Math.abs(Math.atan2(Math.sin(a - b), Math.cos(a - b)));
  // Each arc gets a small number inside the ring (named in the legend). The
  // marker sits mid-arc unless a red spoke or another marker is too close; then
  // it moves to whichever end of its arc is clearest. A spoke at the middle node
  // is half a slot (~9px) from the arc's end, which clears a 1–2 digit number.
  const placed: number[] = [];
  const score = (a: number) =>
    Math.min(
      ...spokeAngles.map((s) => angleDist(a, s) / MARK_CLEAR),
      ...placed.map((m) => angleDist(a, m) / MARK_SPACING),
      Infinity
    );
  const groupItems = groups.map((g, i) => {
    const mid = (g.start + g.end) / 2;
    const markAngle = score(mid) >= 1 ? mid : [mid, g.start, g.end].reduce((a, b) => (score(b) > score(a) ? b : a));
    placed.push(markAngle);
    return { ...g, index: i + 1, mark: polar(MARK_R, markAngle) };
  });
  const ys = [...nodeLabels.values()].map((l) => l.y);
  const top = Math.min(C - R - 24, ...ys.map((y) => y - 16));
  const bottom = Math.max(C + R + 24, ...ys.map((y) => y + 16));
  const dim = (category: string) => hovered !== null && hovered !== category;

  return (
    <div className="rounded-2xl border border-border-light bg-surface-primary p-4">
      {/* Legend beside the ring only from xl: below that a fixed side column
          squeezes the SVG, so the diagram stacks above the legend instead. */}
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_13rem] xl:items-center">
        <svg
          viewBox={`0 ${top} ${W} ${bottom - top}`}
          className="block h-auto w-full"
          role="img"
          aria-label={`${nodes.length} integrations, ${counts.failing} failing`}
        >
          {/* Spokes only where something is broken, so the eye goes straight there */}
          {nodes
            .filter((n) => n.health === 'failing')
            .map((n) => {
              const p = polar(R - 6, n.angle);
              return (
                <line key={`spoke-${n.id}`} x1={CX} y1={C} x2={p.x} y2={p.y} stroke={NODE_FILL.failing} strokeWidth={1.5} />
              );
            })}

          {/* Category arcs with a number; names and counts live in the legend beside the ring */}
          {groupItems.map((g) => (
            <g
              key={`group-${g.category}`}
              onMouseEnter={() => setHovered(g.category)}
              onMouseLeave={() => setHovered(null)}
              opacity={dim(g.category) ? 0.35 : 1}
            >
              <title>{`${g.category} · ${g.count}`}</title>
              <path
                d={arcPath(ARC_R, g.start + 0.01, g.end - 0.01)}
                fill="none"
                stroke={hovered === g.category ? '#121212' : '#C9C4BC'}
                strokeWidth={hovered === g.category ? 3 : 2}
                strokeLinecap="round"
              />
              <text
                x={g.mark.x}
                y={g.mark.y}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={9}
                fontWeight={600}
                fill={hovered === g.category ? '#121212' : '#8A857D'}
              >
                {g.index}
              </text>
            </g>
          ))}

          {/* Hub */}
          <circle cx={CX} cy={C} r={44} fill="#121212" />
          <text x={CX} y={C - 4} textAnchor="middle" fill="#ffffff" fontSize={12} fontWeight={700} letterSpacing="0.05em">
            SEQUESTO
          </text>
          <text x={CX} y={C + 12} textAnchor="middle" fill="#a3a3a3" fontSize={10} fontWeight={500}>
            OS
          </text>

          {/* Nodes with horizontal labels; a faint leader joins a node to a label that had to move */}
          {nodes.map((n) => {
            const p = polar(R, n.angle);
            const l = nodeLabels.get(n)!;
            const isError = n.health === 'failing';
            const tooltip = [
              n.name,
              n.providerName !== n.name ? n.providerName : null,
              n.category,
              HEALTH_LABEL[n.health],
              n.reason ? failureCause(n.reason) : null,
              n.lastActivityAt ? `Last activity ${formatDayMonthYearTime(n.lastActivityAt)}` : null,
            ]
              .filter(Boolean)
              .join(' · ');
            const lx = l.x + l.side * 4;
            return (
              <g
                key={n.id}
                onClick={() => select(n.id)}
                onMouseEnter={() => setHovered(n.category)}
                onMouseLeave={() => setHovered(null)}
                opacity={dim(n.category) ? 0.35 : 1}
                className="cursor-pointer [&:hover_text]:fill-[#121212]"
              >
                <title>{tooltip}</title>
                <line x1={p.x} y1={p.y} x2={lx} y2={l.y} stroke="#E4E0DA" strokeWidth={1} />
                <circle cx={p.x} cy={p.y} r={isError ? 6 : 4.5} fill={NODE_FILL[n.health]} stroke="#ffffff" strokeWidth={1.5} />
                <text
                  x={lx + l.side * 3}
                  y={l.y}
                  textAnchor={l.side === 1 ? 'start' : 'end'}
                  dominantBaseline="middle"
                  fontSize={12}
                  fontWeight={isError ? 700 : 500}
                  fill={isError ? '#b91c1c' : '#525252'}
                >
                  {truncate(n.name, MAX_LABEL)}
                </text>
              </g>
            );
          })}
        </svg>

        {/* Category legend: numbered clockwise from the top, matching the markers on the ring */}
        <div>
          <p className="eyebrow mb-2">Teams · clockwise</p>
          <ol className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-body-xs sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-1">
            {groupItems.map((g) => (
              <li
                key={g.category}
                onMouseEnter={() => setHovered(g.category)}
                onMouseLeave={() => setHovered(null)}
                className={`flex cursor-default items-baseline gap-2 rounded-sm px-1.5 py-0.5 ${
                  hovered === g.category ? 'bg-surface-secondary text-text-primary' : 'text-text-secondary'
                }`}
              >
                <span className="w-4 flex-none text-right font-semibold tabular-nums text-text-tertiary">{g.index}</span>
                <span className="min-w-0 flex-1 truncate">{g.category}</span>
                <span className="tabular-nums text-text-tertiary">{g.count}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {/* Status legend */}
      <div className="mt-2 flex flex-wrap items-center justify-between gap-3 border-t border-border-light pt-3 text-body-xs text-text-secondary">
        <div className="flex flex-wrap items-center gap-4">
          {legend.map((h) => (
            <span key={h} className="flex items-center gap-1.5">
              <span className={`h-2.5 w-2.5 rounded-full ${HEALTH_DOT[h]}`} />
              {HEALTH_LABEL[h]} <span className="tabular-nums text-text-tertiary">{counts[h]}</span>
            </span>
          ))}
        </div>
        <span className="text-text-tertiary">Hover a name for details, click to configure</span>
      </div>
    </div>
  );
}
